import React, { useState, useCallback } from 'react';
import { useTheme } from '../../utils/theme';
import { Clock, Target, AlertCircle, CheckCircle, Zap, RefreshCw } from 'lucide-react';

/**
 * EndGameDecision - End-game decision support UI
 *
 * Provides probability-based recommendations for:
 * - Foul or no foul strategies
 * - Final shot selection
 * - Timeout optimization
 */

const SCENARIOS = [
  { id: 'up3_defense', label: 'Up 3, They Have Ball', scoreDiff: 3, possession: 'them' },
  { id: 'down2_offense', label: 'Down 2, We Have Ball', scoreDiff: -2, possession: 'us' },
  { id: 'down3_offense', label: 'Down 3, We Have Ball', scoreDiff: -3, possession: 'us' },
  { id: 'up1_offense', label: 'Up 1, We Have Ball', scoreDiff: 1, possession: 'us' },
  { id: 'down1_offense', label: 'Down 1, We Have Ball', scoreDiff: -1, possession: 'us' },
  { id: 'tied_offense', label: 'Tied, We Have Ball', scoreDiff: 0, possession: 'us' }
];

const TIME_OPTIONS = [24, 15, 10, 8, 5, 3];

// Simulate API call for decisions
async function getEndGameDecision(situation) {
  // In production, this would call /api/basketball/end-game
  await new Promise(resolve => setTimeout(resolve, 500));

  const { scoreDiff, timeRemaining, possession } = situation;
  const deficit = Math.abs(scoreDiff);

  // Defensive when leading
  if (possession === 'them' && scoreDiff > 0) {
    const threePtMakePct = 0.32;
    const ftPct = 0.72;

    return {
      scenario: 'DEFENSIVE_LEADING',
      recommendation: 'dont_foul',
      recommendedAction: "Don't Foul (Until 4s)",
      winProbability: Math.round((1 - threePtMakePct) * 1000) / 10,
      options: [
        {
          id: 'dont_foul',
          name: "Don't Foul",
          winProbability: 68.0,
          reasoning: [
            `Their 3PT%: ${(threePtMakePct * 100).toFixed(1)}%`,
            'Clutch shooting typically lower',
            `They tie ${(threePtMakePct * 100).toFixed(1)}% of the time`
          ]
        },
        {
          id: 'foul_now',
          name: 'Foul Immediately',
          winProbability: 71.2,
          reasoning: [
            `Their FT%: ${(ftPct * 100).toFixed(1)}%`,
            `If they make both: up 1 with ${timeRemaining}s`,
            'Risk: Putback 3PT or foul on shot'
          ]
        },
        {
          id: 'foul_late',
          name: `Foul at ${Math.max(2, timeRemaining - 4)}s`,
          winProbability: 74.8,
          reasoning: [
            'Preserves defensive pressure',
            'Forces FTs with less time',
            'Limits putback opportunities'
          ]
        }
      ],
      summary: `Up ${scoreDiff} with ${timeRemaining}s - Don't foul initially, but foul at 4s if they haven't shot`
    };
  }

  // Offensive when trailing
  if (possession === 'us' && scoreDiff < 0) {
    const twoMakePct = 0.52;
    const threeMakePct = 0.34;

    const options = [];

    if (deficit <= 2) {
      options.push({
        id: 'go_for_2',
        name: 'Go for 2 (Tie)',
        winProbability: deficit === 1 ? 52.0 : 26.0,
        reasoning: [
          `2PT make rate: ${(twoMakePct * 100).toFixed(1)}%`,
          deficit === 2 ? 'OT is ~50/50' : 'Direct path to victory',
          'Higher percentage shot'
        ]
      });
    }

    if (deficit <= 3) {
      options.push({
        id: 'go_for_3',
        name: 'Go for 3 (Win)',
        winProbability: 34.0,
        reasoning: [
          `3PT make rate: ${(threeMakePct * 100).toFixed(1)}%`,
          'Wins game immediately',
          'No overtime needed'
        ]
      });
    }

    options.sort((a, b) => b.winProbability - a.winProbability);

    return {
      scenario: 'OFFENSIVE_TRAILING',
      recommendation: options[0].id,
      recommendedAction: options[0].name,
      winProbability: options[0].winProbability,
      options,
      shotSelection: {
        primaryTarget: deficit >= 3 ? '3PT' : '3PT to win',
        recommendations: [
          { option: '3PT to win', probability: '~34%', reasoning: 'Wins outright, no OT risk' },
          { option: '2PT to tie', probability: deficit === 1 ? '~52%' : '~26%', reasoning: 'Higher make%, but OT is 50/50' }
        ]
      },
      summary: `Down ${deficit} with ${timeRemaining}s - ${options[0].name} is recommended (${options[0].winProbability}% win probability)`
    };
  }

  // Ball control when leading
  return {
    scenario: 'BALL_CONTROL',
    recommendation: 'run_clock',
    recommendedAction: 'Run Clock / Ball Control',
    winProbability: 85 + Math.min(scoreDiff * 2, 10),
    options: [
      {
        id: 'run_clock',
        name: 'Run Clock / Ball Control',
        winProbability: 90,
        reasoning: [
          'Maximize clock usage',
          'Force them to foul',
          'Take high-percentage shot late'
        ]
      }
    ],
    summary: `Up ${scoreDiff} with ${timeRemaining}s - Run clock, force them to foul`
  };
}

function OptionCard({ option, isRecommended, isDarkMode, onClick }) {
  const bgColor = isRecommended
    ? 'bg-orange-500/20 border-orange-500'
    : 'bg-gray-800 border-gray-700';

  return (
    <button
      onClick={onClick}
      className={`w-full p-4 rounded-xl border-2 text-left transition-all hover:scale-[1.02] ${bgColor}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            {isRecommended && <CheckCircle className="text-orange-500" size={18} />}
            <h4 className={`font-semibold ${'text-white'}`}>
              {option.name}
            </h4>
          </div>
          <div className="mt-2 space-y-1">
            {option.reasoning.map((reason, i) => (
              <p key={i} className={`text-sm ${'text-gray-400'}`}>
                {reason}
              </p>
            ))}
          </div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${
            option.winProbability >= 70 ? 'text-green-500' :
              option.winProbability >= 50 ? 'text-yellow-500' : 'text-orange-500'
          }`}>
            {option.winProbability}%
          </div>
          <div className={`text-xs ${'text-gray-500'}`}>
            Win Prob
          </div>
        </div>
      </div>
    </button>
  );
}

export default function EndGameDecision() {
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark

  const [selectedScenario, setSelectedScenario] = useState(SCENARIOS[0]);
  const [timeRemaining, setTimeRemaining] = useState(8);
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(false);

  const analyzeScenario = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getEndGameDecision({
        scoreDiff: selectedScenario.scoreDiff,
        timeRemaining,
        possession: selectedScenario.possession
      });
      setDecision(result);
    } catch (error) {
      console.error('Failed to analyze scenario:', error);
    }
    setLoading(false);
  }, [selectedScenario, timeRemaining]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${'text-white'}`}>
            End-Game Decision Engine
          </h3>
          <p className={`text-sm ${'text-gray-400'}`}>
            Probability-based clutch situation analysis
          </p>
        </div>
        <Clock className="text-orange-500" size={24} />
      </div>

      {/* Scenario Selection */}
      <div className={`p-4 rounded-xl ${'bg-gray-900 border border-gray-800'}`}>
        <h4 className={`text-sm font-medium mb-3 ${'text-gray-300'}`}>
          Select Scenario
        </h4>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
          {SCENARIOS.map(scenario => (
            <button
              key={scenario.id}
              onClick={() => setSelectedScenario(scenario)}
              className={`p-3 rounded-lg text-sm font-medium transition-all ${
                selectedScenario.id === scenario.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {scenario.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className={`text-sm ${'text-gray-400'}`}>
              Time Remaining
            </label>
            <div className="flex gap-2 mt-1">
              {TIME_OPTIONS.map(time => (
                <button
                  key={time}
                  onClick={() => setTimeRemaining(time)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    timeRemaining === time
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  {time}s
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={analyzeScenario}
            disabled={loading}
            className={`px-6 py-3 rounded-xl font-semibold flex items-center gap-2 ${
              loading
                ? 'bg-gray-500 text-gray-300'
                : 'bg-orange-500 text-white hover:bg-orange-600'
            } transition-all`}
          >
            {loading ? <RefreshCw className="animate-spin" size={18} /> : <Zap size={18} />}
            {loading ? 'Analyzing...' : 'Analyze'}
          </button>
        </div>
      </div>

      {/* Results */}
      {decision && (
        <div className="space-y-4">
          {/* Summary Banner */}
          <div className={`p-4 rounded-xl ${
            decision.winProbability >= 70
              ? 'bg-green-500/10 border border-green-500/30'
              : decision.winProbability >= 50
                ? 'bg-yellow-500/10 border border-yellow-500/30'
                : 'bg-orange-500/10 border border-orange-500/30'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-sm ${'text-gray-400'}`}>
                  Recommended Action
                </div>
                <div className={`text-xl font-bold ${'text-white'}`}>
                  {decision.recommendedAction}
                </div>
              </div>
              <div className="text-right">
                <div className={`text-3xl font-bold ${
                  decision.winProbability >= 70 ? 'text-green-500' :
                    decision.winProbability >= 50 ? 'text-yellow-500' : 'text-orange-500'
                }`}>
                  {decision.winProbability}%
                </div>
                <div className={`text-sm ${'text-gray-500'}`}>
                  Win Probability
                </div>
              </div>
            </div>
            <p className={`mt-2 text-sm ${'text-gray-400'}`}>
              {decision.summary}
            </p>
          </div>

          {/* Options */}
          <div>
            <h4 className={`text-sm font-medium mb-3 ${'text-gray-300'}`}>
              All Options
            </h4>
            <div className="space-y-3">
              {decision.options.map(option => (
                <OptionCard
                  key={option.id}
                  option={option}
                  isRecommended={option.id === decision.recommendation}
                  isDarkMode={isDarkMode}
                  onClick={() => {}}
                />
              ))}
            </div>
          </div>

          {/* Shot Selection (if applicable) */}
          {decision.shotSelection && (
            <div className={`p-4 rounded-xl ${'bg-gray-900 border border-gray-800'}`}>
              <h4 className={`text-sm font-medium mb-3 flex items-center gap-2 ${'text-gray-300'}`}>
                <Target size={16} />
                Shot Selection
              </h4>
              <div className={`text-lg font-bold mb-2 ${'text-white'}`}>
                Primary Target: {decision.shotSelection.primaryTarget}
              </div>
              <div className="space-y-2">
                {decision.shotSelection.recommendations.map((rec, i) => (
                  <div
                    key={i}
                    className={`p-2 rounded-lg flex items-center justify-between ${
                      'bg-gray-800'
                    }`}
                  >
                    <div>
                      <span className={`font-medium ${'text-white'}`}>
                        {rec.option}
                      </span>
                      <p className={`text-xs ${'text-gray-400'}`}>
                        {rec.reasoning}
                      </p>
                    </div>
                    <span className={`font-bold ${'text-orange-400'}`}>
                      {rec.probability}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Initial State */}
      {!decision && !loading && (
        <div className={`p-8 rounded-xl text-center ${'bg-gray-900/50'}`}>
          <AlertCircle size={48} className={`mx-auto mb-3 ${'text-gray-600'}`} />
          <p className={'text-gray-400'}>
            Select a scenario and click "Analyze" to get recommendations
          </p>
        </div>
      )}
    </div>
  );
}
